import { TokenProvider, TokenRequestOptions } from '@fluidx/office-fluid-types';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { OdcApiSiteOrigin } from '@fluidframework/odsp-driver';
import { RetryPolicy } from '../fetch/fetchWithRetry';
import { AuthorizedFetchProps, authorizedFetchWithRetry, instrumentedAuthorizedFetchWithRetry } from '../fetch';
import { getOdspAndGraphRequestIdsFromResponse } from '../fetch/getOdspAndGraphRequestIdsFromResponse';

/**
 * Describes a Single SharePoint Group backed Site
 */
export interface SharePointGroupSite {
  /** Title of the site, used anywhere we need to display the site in human readable form */
  title: string;
  /** URL to the site banner image */
  bannerImageUrl: string;
  /** webUrl of the site */
  url: string;
  /** groupId of the group the site belongs to, needed to get the driveId */
  groupId: string;
  /** SiteId. If groupId is not supplied and this id is, we will use it to get the driveId */
  id: string;
}

export interface SharePointGroupSiteWithDriveId extends SharePointGroupSite {
  /** driveId used to access Fluid files */
  driveId: string;
}

export interface GraphIdentity {
  displayName: string;
  email: string;
  id: string;
}

export interface GraphIdentitySet {
  application: GraphIdentity;
  device: GraphIdentity;
  user: GraphIdentity;
}

export interface GraphHashesType {
  sha1Hash: string; // SHA1 hash of file contents
  crc32Hash: string; // CRC32 value of file
}

export interface GraphFile {
  hashes: GraphHashesType;
  mimeType: string;
}

export interface GraphFolder {
  childCount: number;
}

export interface GraphItemReference {
  driveId: string;
  id: string;
  path: string;
}

export interface GraphShared {
  owner: GraphIdentitySet;
  scope: string;
  sharedBy: GraphIdentitySet;
  sharedDateTime: string;
}

export interface GraphSharepointIds {
  listId: string;
  listItemId: string;
  listItemUniqueId: string;
  siteId: string;
  siteUrl: string;
  webId: string;
}

export interface GraphSpecialFolder {
  name: string; // identifier in the drive/special collection
}

export interface GraphThumbnail {
  height: number;
  width: number;
  url: string;
  sourceItemId: string;
  content: any;
}

export interface GraphThumbnailSet {
  id: string;
  source: GraphThumbnail;
  small: GraphThumbnail;
  medium: GraphThumbnail;
  large: GraphThumbnail;
}

export interface GraphPerson {
  userPrincipalName: string;
  displayName?: string;
  jobTitle?: string;
  mail?: string;
  companyName?: string;
  scoredEmailAddresses?: { address: string }[];
}

export interface GraphPersonPicture {
  userPrincipalName: string;
  pictureUrl: string;
}

export interface GraphBase {
  createdDateTime: string;
  createdBy: GraphIdentitySet;
  id: string;
  lastModifiedBy: GraphIdentitySet;
  lastModifiedDateTime: string;
  webUrl: string;
}

export interface SharePointIds {
  listId: string;
  siteId: string;
  siteUrl: string;
  webId: string;
}

export interface GraphDrive extends GraphBase {
  driveType: string;
  owner: GraphIdentitySet;
  quota: any;
  description: string;
  name: string;
  sharePointIds?: SharePointIds;
}

export interface GraphDriveItem extends GraphBase {
  audio: any; // audio metadata for audio files
  cTag: string;
  content: any; // file content stream
  deleted: any; // item was deleted if present
  eTag: string;
  file: GraphFile;
  fileSystemInfo: any; // creation dates from originating client
  folder: GraphFolder; // contains number of children if item is a folder
  image: any; // image metadata for image files
  location: any; // location metadata if the item has a location
  name: string;
  package: any; // present if item is a package
  parentReference: GraphItemReference;
  photo: any; // photo metadata if the item is a photo
  remoteItem: GraphDriveItem; // indicates item exists in another drive
  searchResult: any; // present if item is a search result
  shared: GraphShared;
  sharepointIds?: GraphSharepointIds;
  size: number;
  specialFolder: GraphSpecialFolder;
  thumbnails?: Array<GraphThumbnailSet>;
  video: any; // video metadata if the item is a video
  webDavUrl: string; // canonical Url for drive item
}

export interface GraphDriveItemCollection {
  value: Array<GraphDriveItem>;
}

export interface DeltaResult {
  deltaLink: string | undefined;
  files: GraphDriveItem[];
}

const graphScopesEnterprise = ['files.readwrite.all', 'people.read', 'user.read', 'user.readbasic.all'];
const graphScopesConsumer = ['files.readwrite', 'people.read', 'user.read'];

export const getGraphToken = (tokenProvider: TokenProvider, options?: TokenRequestOptions) =>
  tokenProvider.getToken(
    tokenProvider.identityType === 'Consumer' ? graphScopesConsumer : graphScopesEnterprise,
    options
  );

/**
 * Issues a graph fetch request
 * @param tokenProvider Token provider than can supply Graph tokens
 * @param graphUrl Url to fetch. Can either be a full URL (e.g. https://graph.microsoft.com/v1.0/me/people) or a partial url (e.g. me/people)
 * @param nameForLogging Name used for logging
 * @param doNotLogOnSuccess Boolean passed to fetchWithRetry. Set this to false if you want fetchWithRetry to log only the failed responses
 * @param logger used to log results of operation, including any error
 * @param tenantId tenant id is used to designate AAD tenant to fetch Graph token from. This can be specified in shape of GUID or
 * domain hostname. If not specified then token will be returned for default authority designated by token provider.
 * @param requestInit Optional. Request Init to be passed to fetch
 * @param retryPolicy Optional. Retry policy to be passed to fetchWithRetry
 * @param timeoutMs Optional. Timeout value to be passed to fetchWithRetry
 */
export async function graphFetch(
  tokenProvider: TokenProvider,
  graphUrl: string,
  nameForLogging?: string,
  logger?: ITelemetryBaseLogger,
  tenantId?: string,
  requestInit?: RequestInit,
  retryPolicy?: RetryPolicy<Response>,
  timeoutMs = 0,
  doNotLogOnSuccess?: boolean
): Promise<Response> {
  const url = graphUrl.startsWith('http') ? graphUrl : `https://graph.microsoft.com/v1.0/${graphUrl}`;
  const authorizedFetchProps: AuthorizedFetchProps = {
    getToken: (options) => getGraphToken(tokenProvider, options),
    url,
    tenantId,
    requestInit,
    retryPolicy,
    timeoutMs
  };
  const fetchResponse =
    logger && nameForLogging
      ? instrumentedAuthorizedFetchWithRetry({
          ...authorizedFetchProps,
          logger,
          nameForLogging,
          doNotLogOnSuccess,
          getAdditionalProps: async (response, _isFinalAttempt) => getOdspAndGraphRequestIdsFromResponse(response)
        })
      : authorizedFetchWithRetry(authorizedFetchProps);

  return (await fetchResponse).result;
}

// Store user picture for the lifetime of the app
let userPicturePromise: Promise<string | number> | undefined;

/**
 * Returns url for referencing user picture blob in case of success or number representing returned status code in case of failure.
 * E.g. 404 will be returned in case user picture was not found.
 * Scope needed: user.read
 */
export function getMyPhoto(tokenProvider: TokenProvider, logger: ITelemetryBaseLogger): Promise<string | number> {
  if (!userPicturePromise) {
    userPicturePromise = new Promise<string | number>(async (resolve, reject) => {
      let userPictureResponse: Response | undefined;
      try {
        userPictureResponse = await graphFetch(
          tokenProvider,
          `${tokenProvider.identityType === 'Consumer' ? 'https://graph.microsoft.com/beta/' : ''}me/photo/$value`,
          'GetMyPhoto',
          logger
        );
      } catch (error) {
        userPicturePromise = undefined;
        reject(error);
      }

      if (userPictureResponse) {
        // Cache result only for 200 (success) and 404 (NotFound) responses
        if (userPictureResponse.status !== 200 && userPictureResponse.status !== 404) {
          userPicturePromise = undefined;
          resolve(userPictureResponse.status);
        } else {
          resolve(
            userPictureResponse.ok ? URL.createObjectURL(await userPictureResponse.blob()) : userPictureResponse.status
          );
        }
      }
    });
  }

  return userPicturePromise;
}

// Store pictures of requested users for the lifetime of the app
const personPicturePromisesCache: { [userPrincipalName: string]: Promise<GraphPersonPicture | number> } = {};

/**
 * In case of
 *  - success: resolves the promise with the URL referencing the picture blob
 *  - failure: resolves the promise with the number representing the response's status code
 *    E.g. the returned promise will be resolved with 404 in case the picture was not found.
 * Scope needed: user.readbasic.all
 */
export function getUserPhoto(
  tokenProvider: TokenProvider,
  userPrincipalName: string,
  logger?: ITelemetryBaseLogger,
  tenantId?: string
): Promise<GraphPersonPicture | number> {
  // TODO: Is there a way to get a photo based on MSA user principal name?
  // users/${userPrincipalName}/photo seem to always return photo for impersonated user instead of specified user.
  if (tokenProvider.identityType === 'Consumer') {
    return Promise.resolve(404);
  }

  if (!personPicturePromisesCache[userPrincipalName]) {
    personPicturePromisesCache[userPrincipalName] = new Promise<GraphPersonPicture | number>(
      async (resolve, reject) => {
        let response: Response | undefined;
        try {
          response = await graphFetch(
            tokenProvider,
            `users/${userPrincipalName}/photos/96x96/$value`,
            'GetUserPhoto',
            logger,
            tenantId
          );
        } catch (error) {
          delete personPicturePromisesCache[userPrincipalName];
          reject(error);
        }

        if (response) {
          // Cache result only for 200 (success) and 404 (NotFound) responses
          if (response.status !== 200 && response.status !== 404) {
            delete personPicturePromisesCache[userPrincipalName];
            resolve(response.status);
          } else {
            resolve(
              response.ok
                ? { userPrincipalName, pictureUrl: URL.createObjectURL(await response.blob()) }
                : response.status
            );
          }
        }
      }
    );
  }
  return personPicturePromisesCache[userPrincipalName];
}

/**
 * Returns list of people relevant for current user.
 * Scope needed: people.read
 */
export async function getPeopleList(
  tokenProvider: TokenProvider,
  numberOfItems: number,
  logger?: ITelemetryBaseLogger,
  filter?: string,
  tenantId?: string
): Promise<GraphPerson[] | number> {
  const isConsumer = tokenProvider.identityType === 'Consumer';
  const graphUrl = `${
    isConsumer
      ? 'me/people?$select=userPrincipalName,displayName,jobTitle,scoredEmailAddresses&'
      : `me/people?$select=userPrincipalName,displayName,jobTitle&$filter=personType/subClass eq 'OrganizationUser'&`
  }$top=${numberOfItems}${filter ? `&$search="${filter}"` : ''}`;

  const response = await graphFetch(tokenProvider, graphUrl, 'GetMyPeople', logger, tenantId, {
    headers: { 'X-PeopleQuery-QuerySources': 'Mailbox,Directory' }
  });

  if (!response.ok) {
    return response.status;
  }

  const result: any = await response.json();
  if (result && result.value) {
    const people = result.value as GraphPerson[];
    if (isConsumer) {
      return people.map((person) => {
        if (
          !person.userPrincipalName &&
          Array.isArray(person.scoredEmailAddresses) &&
          person.scoredEmailAddresses.length
        ) {
          person.userPrincipalName = person.scoredEmailAddresses[0].address;
        }
        return person;
      });
    }
    return people;
  }

  return [];
}

// Store companyName for the lifetime of the session
let companyNamePromise: Promise<string | number> | undefined;

/**
 * Returns the companyName of the current logged in user or returns the response status code in case of failure
 * E.g. 404 will be returned in the case user details were not found.
 * Scope needed: user.read
 */
export function getMyCompanyName(
  tokenProvider: TokenProvider,
  logger?: ITelemetryBaseLogger
): Promise<string | number> {
  if (!companyNamePromise) {
    companyNamePromise = new Promise<string | number>(async (resolve, reject) => {
      let response: Response | undefined;
      try {
        response = await graphFetch(tokenProvider, `organization?$select=displayName`, 'GetMyCompanyName', logger);
      } catch (error) {
        companyNamePromise = undefined;
        reject(error);
      }

      if (response) {
        // Cache result only for 200 (success) and 404 (NotFound) responses
        if (response.status !== 200 && response.status !== 404) {
          companyNamePromise = undefined;
          resolve(response.status);
        } else {
          const result = await response.json();
          const displayName = result?.value?.[0]?.displayName;

          if (displayName) {
            resolve(displayName);
          } else {
            resolve(response.status);
          }
        }
      }
    });
  }

  return companyNamePromise;
}

// Store details of the requested users for the lifetime of the app
const graphPersonPromisesCache: { [userPrincipalName: string]: Promise<GraphPerson | number> } = {};

/**
 * Returns the details for the given user or returns the response status code in case of failure
 * E.g. 404 will be returned in the case user details were not found.
 * Scope needed: user.readbasic.all
 */
export function getPersonDetails(
  tokenProvider: TokenProvider,
  userPrincipalName: string,
  logger?: ITelemetryBaseLogger,
  tenantId?: string
): Promise<GraphPerson | number> {
  if (!graphPersonPromisesCache[userPrincipalName]) {
    graphPersonPromisesCache[userPrincipalName] = new Promise<GraphPerson | number>(async (resolve, reject) => {
      let response: Response | undefined;
      try {
        response = await graphFetch(
          tokenProvider,
          `users/${userPrincipalName}?$select=displayName,mail`,
          'GetUserDetails',
          logger,
          tenantId
        );
      } catch (error) {
        delete graphPersonPromisesCache[userPrincipalName];
        reject(error);
      }

      if (response) {
        // Cache result only for 200 (success) and 404 (NotFound) responses
        if (response.status !== 200 && response.status !== 404) {
          delete graphPersonPromisesCache[userPrincipalName];
          resolve(response.status);
        } else {
          const { displayName, mail } = await response.json();

          // TODO: Tokens: With the current tokens we cannot fetch the department name nor the job title.
          // This requires user.read.all and we are currently using user.readbasic.all
          // https://docs.microsoft.com/en-us/graph/permissions-reference
          resolve(response.ok ? { userPrincipalName, displayName, mail } : response.status);
        }
      }
    });
  }
  return graphPersonPromisesCache[userPrincipalName];
}

let myDrive: Promise<SharePointGroupSiteWithDriveId | undefined> | undefined = undefined;

/**
 * Returns personal OneDrive info for current user.
 * Scope needed: files.read
 */
export function getMyDrive(
  tokenProvider: TokenProvider,
  logger: ITelemetryBaseLogger
): Promise<SharePointGroupSiteWithDriveId | undefined> {
  if (myDrive) {
    return myDrive;
  }

  const isConsumer = tokenProvider.identityType === 'Consumer';
  myDrive = graphFetch(
    tokenProvider,
    `me/drive?select=owner,id${isConsumer ? '' : ',sharepointIds'}`,
    'GetMyDrive',
    logger
  )
    .then(async (response) => {
      if (!response.ok) {
        myDrive = undefined;
        return undefined;
      }
      const responseObj: GraphDrive = await response.json();
      return {
        // IMPORTANT: ODC deviates from ODSP in that its file site url is different from vroom api site url.
        // The following code passes vroom api site url instead of file site url for Consumer.
        url: isConsumer ? OdcApiSiteOrigin : responseObj.sharePointIds!.siteUrl,
        title: `${responseObj.owner.user ? responseObj.owner.user.displayName : ''}`,
        driveId: responseObj.id,
        groupId: '',
        bannerImageUrl: '',
        id: isConsumer ? responseObj.id : responseObj.sharePointIds!.siteId
      };
    })
    .catch((error) => {
      myDrive = undefined;
      throw error;
    });

  return myDrive;
}
